import { booleanAttribute, ChangeDetectionStrategy, Component, computed, input, model } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DsButtonModule, DsCheckboxModule, DsLabelModule, DsMessageModule } from '@bmw-ds/components';

import { CustomerVehicleRelationType } from '../../../customer/model/customer-vehicle.model';
import { I18nInterpolateDirective } from '../../../i18n/components/i18n-interpolate.directive';
import { I18nComponent } from '../../../i18n/components/i18n.component';
import { useI18n } from '../../../i18n/services/i18n.service';

@Component({
  selector: 'cdf-add-relation',
  imports: [
    DsButtonModule,
    DsCheckboxModule,
    DsLabelModule,
    FormsModule,
    I18nComponent,
    I18nInterpolateDirective,
    DsMessageModule,
  ],
  host: { class: 'd-flex flex-column gap-3x' },
  template: `
    <p class="text-regular">
      {{ texts().firstParagraph }}
    </p>

    <ng-content />

    <p class="text-regular">
      {{ texts().mainMessage }}
    </p>

    <ds-checkbox-group size="lg">
      @for (relation of availableRelations(); track relation.value) {
        <input
          ds-input
          type="checkbox"
          id="add-relation-{{ relation.value }}"
          [ngModel]="isSelected(relation.value)"
          (ngModelChange)="setRelation(relation.value, $event)"
        />
        <label ds-label for="add-relation-{{ relation.value }}">
          {{ relation.label }}
          @if (otherCustomerRelations()?.[relation.value] === '') {
            ({{ i18n('vehicles.addRelationVerifyModal.replaceUnnamed') }})
          } @else if (otherCustomerRelations()?.[relation.value]; as name) {
            (<i18n key="vehicles.addRelationVerifyModal.replaceName">
              <strong *i18nInterpolate="'name'">{{ name }}</strong> </i18n
            >)
          }
        </label>
      } @empty {
        <ds-message variant="slim" tone="critical" [messageText]="i18n('customerVehicleRelation.noRelationsToAdd')" />
      }
    </ds-checkbox-group>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AddRelationModalComponent {
  protected readonly i18n = useI18n();

  readonly selectedRelations = model.required<ReadonlySet<CustomerVehicleRelationType>>();
  readonly existingRelations = input<CustomerVehicleRelationType[]>([]);
  readonly customerFocus = input(false, { transform: booleanAttribute });
  readonly otherCustomerRelations = input<Partial<Record<CustomerVehicleRelationType, string>>>();

  protected readonly availableRelations = computed(() => {
    return Object.values(CustomerVehicleRelationType)
      .filter((relation) => !this.existingRelations().includes(relation))
      .map((relation) => ({
        value: relation,
        label: this.i18n.enum(relation, 'customerVehicleRelation.enum'),
      }));
  });

  protected readonly texts = computed(() => {
    if (this.customerFocus()) {
      return {
        firstParagraph: this.i18n('vehicles.addCustomerToVehicle.addRelationToFollowingCustomer'),
        mainMessage: this.i18n('vehicles.addCustomerToVehicle.mainMessage'),
      };
    } else {
      return {
        firstParagraph: this.i18n('vehicles.addRelationVerifyModal.addRelationToFollowingVehicle'),
        mainMessage: this.i18n('vehicles.addRelationVerifyModal.mainMessage'),
      };
    }
  });

  protected isSelected(relation: CustomerVehicleRelationType): boolean {
    return this.selectedRelations().has(relation);
  }

  protected setRelation(relation: CustomerVehicleRelationType, selected: boolean): void {
    const newSelectedRelations = new Set(this.selectedRelations());
    if (selected) {
      newSelectedRelations.add(relation);
    } else {
      newSelectedRelations.delete(relation);
    }
    this.selectedRelations.set(newSelectedRelations);
  }
}
